import React from "react";
import appConfig from "../../../../appConfig";
import Card from "../../theme/untitled/Card/Card";
import Button from "../../theme/untitled/Button/Button";
import Icon from "../../theme/untitled/icons/Icon";
import UploadTile from "./UploadTile";
import ColorRow from "./ColorRow";
import { parseGeneralError } from "./errorHelpers";
import styles from "./BrandingPage.module.scss";

const Form = ({
  resetKey,
  draft,
  setField,
  onCancel,
  onSubmit,
  isDirty,
  colorsValid,
  saving,
  anyUploading,
  uploading,
  uploadErrors,
  editError,
  justSaved,
}) => {
  const canSave = isDirty && colorsValid && !saving;

  const generalError = editError
    ? parseGeneralError(
        editError,
        "The service is unavailable. Your changes are still here — try again in a moment."
      )
    : null;

  return (
    <div className={styles.page}>
      <Card aria-labelledby="brand-elements-title" className={styles.brandCard}>
        <Card.Header
          id="brand-elements-title"
          title="Brand elements"
          subtitle={`Used across every widget in this workspace. End-users see these, not your ${appConfig.displayName} account.`}
        />
        <form onSubmit={onSubmit}>
          <Card.Body noPadding className={styles.cardBody}>
            <div className={styles.band}>
              <UploadTile
                key={`favicon-${resetKey}`}
                variant="icon"
                title="Icon"
                tooltip="A smaller representation of your logo, shown in compact placements like widget headers and favicons."
                description="Square mark for compact placements — widget headers and nfavicons. PNG or JPEG, 512 KB max."
                value={draft.favicon}
                unsetMessage="Widgets show your workspace initials until you add one."
                onChange={setField("favicon")}
                uploading={uploading.favicon}
                uploadError={uploadErrors.favicon}
                disabled={anyUploading && !uploading.favicon}
              />
              <UploadTile
                key={`logo-${resetKey}`}
                variant="logo"
                title="Logo"
                tooltip="The full-sized version of your logo, shown in wide placements."
                description="Full lockup for wide placements. Never cropped. PNG or JPEG, 512 KB max."
                value={draft.logo}
                unsetMessage="Optional. Wide placements fall back to the icon."
                onChange={setField("logo")}
                uploading={uploading.logo}
                uploadError={uploadErrors.logo}
                disabled={anyUploading && !uploading.logo}
              />
            </div>
            <div className={styles.band}>
              <ColorRow
                title="Brand color"
                tooltip="Add a splash of color to your branded pages."
                description="Primary surfaces and text in your widgets."
                value={draft.brandColor}
                onChange={setField("brandColor")}
                disabled={saving}
              />
              <ColorRow
                title="Accent color"
                tooltip="Used for the main colors across the pages."
                description="Buttons, links and highlights."
                value={draft.accentColor}
                onChange={setField("accentColor")}
                disabled={saving}
              />
            </div>
          </Card.Body>

          <Card.Footer>
            {generalError && (
              <span
                className={`${styles.footerMessage} ${styles["footerMessage--error"]}`}
              >
                <Icon
                  name="AlertCircle"
                  size={16}
                  className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--error"]}`}
                />
                <span>
                  <strong>Couldn't save your brand elements.</strong>{" "}
                  {generalError}
                </span>
              </span>
            )}
            {!generalError && justSaved && !isDirty && (
              <span
                className={`${styles.footerMessage} ${styles["footerMessage--success"]}`}
              >
                <Icon
                  name="CheckCircle"
                  size={16}
                  className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--success"]}`}
                />
                <span>Saved just now</span>
              </span>
            )}

            <div className={styles.cardFooterActions}>
              <Button
                hierarchy="secondaryGray"
                type="button"
                onClick={onCancel}
                disabled={!isDirty || saving}
              >
                Cancel
              </Button>
              <Button
                hierarchy="primary"
                type="submit"
                disabled={!canSave}
                loading={saving}
              >
                {saving
                  ? "Saving…"
                  : generalError
                  ? "Try again"
                  : "Save changes"}
              </Button>
            </div>
          </Card.Footer>
        </form>
      </Card>
    </div>
  );
};

export default Form;
